import { authStore } from '../modules/auth/auth.store';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { router } from '../app/router';
import { showToast } from '../shared/components/Toast';

export function renderSafetyPage(container: HTMLElement): () => void {
  container.innerHTML = `
    <main class="section pt-nav">
      <div class="container max-w-lg">
        <div class="section-header text-center">
          <div class="badge badge-accent mb-2"><i class="fa-solid fa-shield-cat"></i> Safety First</div>
          <h2>Safe, Transparent & Secure</h2>
          <p class="section-subtitle">We prioritize community respect, zero tolerance for harassment, and full data privacy compliance.</p>
        </div>

        <div class="grid grid-3 gap-6 mb-12">
          <div class="card card-body text-center">
            <i class="fa-solid fa-camera-retro text-3xl mb-4" style="color:var(--color-accent)"></i>
            <h4 class="mb-2">Photo Verification</h4>
            <p>Look for the green checkmark badge before sharing direct contact details.</p>
          </div>
          <div class="card card-body text-center">
            <i class="fa-solid fa-location-dot text-3xl mb-4" style="color:var(--color-accent)"></i>
            <h4 class="mb-2">Public Meeting Spots</h4>
            <p>Always meet first dates in safe, well-lit public venues.</p>
          </div>
          <div class="card card-body text-center">
            <i class="fa-solid fa-user-slash text-3xl mb-4" style="color:var(--color-accent)"></i>
            <h4 class="mb-2">Report & Block</h4>
            <p>Flag suspicious behavior. Our moderation team reviews all reports 24/7.</p>
          </div>
        </div>

        <div class="card card-body">
          <h3 class="mb-4">Community Guidelines</h3>
          <ul class="flex flex-col gap-3 mb-6 text-sm" style="color:var(--color-text-secondary)">
            <li><i class="fa-solid fa-check" style="color:var(--color-success)"></i> Be respectful and authentic in all interactions</li>
            <li><i class="fa-solid fa-check" style="color:var(--color-success)"></i> Zero tolerance for harassment, hate speech, or offensive media</li>
            <li><i class="fa-solid fa-check" style="color:var(--color-success)"></i> Use your authentic photos — catfishing leads to immediate permanent bans</li>
            <li><i class="fa-solid fa-check" style="color:var(--color-success)"></i> Report suspicious profiles immediately</li>
            <li><i class="fa-solid fa-check" style="color:var(--color-success)"></i> Never send money or financial information to strangers</li>
          </ul>

          <h3 class="mb-4">Report a User</h3>
          <form id="reportForm" class="flex flex-col gap-4">
            <div class="form-group">
              <label class="form-label">User Name or Profile ID</label>
              <input type="text" id="reportUser" class="form-input" placeholder="e.g. Amina or demo-1" required>
            </div>
            <div class="form-group">
              <label class="form-label">Reason</label>
              <select id="reportReason" class="form-select" required>
                <option value="">Select a reason</option>
                <option value="harassment">Harassment / Bullying</option>
                <option value="fake_profile">Fake Profile / Catfishing</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="spam">Spam / Financial Scam</option>
                <option value="other">Other Violation</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Description</label>
              <textarea id="reportDescription" class="form-input" rows="3" placeholder="Describe what happened in detail..." required></textarea>
            </div>
            <button type="submit" class="btn btn-primary w-full" id="submitReportBtn">
              <i class="fa-solid fa-flag"></i> Submit Report
            </button>
          </form>
        </div>
      </div>
    </main>
  `;

  const form = container.querySelector('#reportForm') as HTMLFormElement;
  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!authStore.isAuthenticated) {
      showToast('Please sign in before submitting a report.', 'info');
      router.navigate('/auth');
      return;
    }

    const btn = container.querySelector('#submitReportBtn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Submitting report…';

    const reportedUser = (container.querySelector('#reportUser') as HTMLInputElement).value.trim();
    const reason = (container.querySelector('#reportReason') as HTMLSelectElement).value;
    const description = (container.querySelector('#reportDescription') as HTMLTextAreaElement).value.trim();

    try {
      const createReport = httpsCallable<{ reportedUser: string; reason: string; description: string }>(functions, 'createReport');
      await createReport({ reportedUser, reason, description });
      showToast('Report submitted! Our moderation team will review it shortly.', 'success');
      form.reset();
    } catch (err) {
      console.warn('Report submission failed:', err);
      showToast('Could not submit the report. Please try again.', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<i class="fa-solid fa-flag"></i> Submit Report';
    }
  };
  form.addEventListener('submit', onSubmit);

  return () => {
    form.removeEventListener('submit', onSubmit);
    container.innerHTML = '';
  };
}
